import { useRef } from 'react'
import PropTypes from 'prop-types'

import { TICKET_ATTACHMENT_ACCEPT, TICKET_ATTACHMENT_MAX_MB } from '../../../../shared/const'

import Btn from '../../../../UI/Btn'

import styles from './attachments.module.scss'

const ERLAUBT = TICKET_ATTACHMENT_ACCEPT.split(',')

/**
 * Trennt eine Dateiliste in zulässige und abgelehnte.
 *
 * Wird auch beim Einfügen aus der Zwischenablage gebraucht — dort kommen die
 * Dateien am Auswahlfeld vorbei und wären sonst ungeprüft.
 *
 * @returns {[File[], string[]]} zulässige Dateien, Gründe für die Ablehnung
 */
export const pruefeAnhaenge = files => {
	const zulaessig = []
	const abgelehnt = []

	for (const file of Array.from(files || [])) {
		const name = file.name || 'Datei'
		if (!ERLAUBT.includes(file.type)) {
			abgelehnt.push(`${name}: Typ nicht erlaubt`)
		} else if (file.size > TICKET_ATTACHMENT_MAX_MB * 1024 * 1024) {
			const mb = (file.size / 1024 / 1024).toFixed(1)
			abgelehnt.push(`${name}: ${mb} MB, erlaubt sind ${TICKET_ATTACHMENT_MAX_MB} MB`)
		} else {
			zulaessig.push(file)
		}
	}

	return [zulaessig, abgelehnt]
}

/**
 * Auswahl von Anhängen — Datei oder Zwischenablage.
 *
 * Wird an drei Stellen gebraucht: beim Anlegen, beim Bearbeiten und in der
 * Detailansicht. Beim Anlegen gibt es die Anforderung noch nicht, an der die
 * Dateien hängen könnten — deshalb sammelt der Aufrufer sie und lädt sie nach
 * dem Speichern hoch. Diese Komponente kennt den Unterschied nicht; sie meldet
 * nur, was ausgewählt wurde.
 *
 * Größe und Typ werden hier schon geprüft, nicht erst am Server. Eine 200-MB-
 * Bildschirmaufnahme erst hochzuladen, um dann eine Absage zu bekommen, ist
 * ärgerlich und langsam. Der Server prüft trotzdem noch einmal — das hier ist
 * Bequemlichkeit, kein Schutz.
 */
export default function AttachmentPicker({ onFiles, disabled, hint }) {
	const input = useRef(null)

	const pruefen = files => {
		if (!files?.length) return
		onFiles(...pruefeAnhaenge(files))
	}

	return (
		<div className={styles.picker}>
			{hint !== false && (
				<span className={styles.hint}>
					Bild aus der Zwischenablage einfügen: Strg+V bzw. Cmd+V · Bilder, PDF und
					Videos bis {TICKET_ATTACHMENT_MAX_MB} MB
				</span>
			)}
			<Btn
				type="button"
				size="small"
				theme="secondary"
				disabled={disabled}
				onClick={() => input.current?.click()}
			>
				Datei wählen
			</Btn>
			<input
				ref={input}
				type="file"
				accept={TICKET_ATTACHMENT_ACCEPT}
				multiple
				className={styles.hiddenInput}
				onChange={event => {
					pruefen(event.target.files)
					// Zuruecksetzen, sonst laesst sich dieselbe Datei nicht ein
					// zweites Mal waehlen - der Wert aendert sich dann nicht.
					event.target.value = ''
				}}
			/>
		</div>
	)
}

AttachmentPicker.propTypes = {
	onFiles: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
	hint: PropTypes.bool
}

AttachmentPicker.defaultProps = {
	disabled: false,
	hint: true
}
